import React, { forwardRef } from 'react';

interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outlined' | 'filled';
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      size = 'md',
      variant = 'default',
      fullWidth = false,
      startIcon,
      endIcon,
      className = '',
      style = {},
      ...props
    },
    ref
  ) => {
    const getSizeClasses = (): string => {
      switch (size) {
        case 'sm':
          return 'h-8 px-2 text-sm';
        case 'lg':
          return 'h-12 px-4 text-lg';
        default:
          return 'h-10 px-3';
      }
    };

    const getVariantStyles = (): React.CSSProperties => {
      switch (variant) {
        case 'outlined':
          return {
            backgroundColor: 'transparent',
            border: '2px solid var(--border)',
            color: 'var(--text-primary)',
            ...style,
          };
        case 'filled':
          return {
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid transparent',
            color: 'var(--text-primary)',
            ...style,
          };
        default:
          return {
            backgroundColor: 'var(--bg-primary)',
            border: '1px solid var(--border)',
            color: 'var(--text-primary)',
            ...style,
          };
      }
    };

    const inputClasses = [
      'input',
      getSizeClasses(),
      fullWidth ? 'w-full' : '',
      error ? 'border-red-500 focus:border-red-500' : 'focus:border-accent',
      startIcon ? 'pl-10' : '',
      endIcon ? 'pr-10' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const inputId =
      props.id || `input-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className={`flex flex-col ${fullWidth ? 'w-full' : ''}`}>
        {label && (
          <label
            htmlFor={inputId}
            className="mb-2 text-sm font-medium"
            style={{ color: 'var(--text-primary)' }}
          >
            {label}
          </label>
        )}
        <div className="relative">
          {startIcon && (
            <div
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              style={{ color: 'var(--text-secondary)' }}
            >
              {startIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={inputClasses}
            style={getVariantStyles()}
            {...props}
          />
          {endIcon && (
            <div
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              style={{ color: 'var(--text-secondary)' }}
            >
              {endIcon}
            </div>
          )}
        </div>
        {(error || helperText) && (
          <div className="mt-1 text-xs">
            {error ? (
              <span className="text-red-500">{error}</span>
            ) : (
              <span style={{ color: 'var(--text-secondary)' }}>
                {helperText}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
